/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Subsidiaries: Two featured panels for flagship brands (Towing AI, AutoShop AI),
 * then a full portfolio grid for all other brands. Each brand has its own accent color
 * and icon. Thick borders, monospace labels, industrial feel.
 */
import Reveal from "./Reveal";
import Img from "./Img";
import {
  Truck, Wrench, ArrowRight, MapPin, BarChart3, Clock, Users, Cog, MessageSquare,
  Car, Gavel, Briefcase, Shield, Landmark, Scale, Hammer, Link2, Radio, Award,
  ExternalLink, ShieldCheck, Navigation, Database, ClipboardList, BookOpen, FileSearch,
  Headphones, ShieldAlert, CreditCard, Newspaper, Hash
} from "lucide-react";

const TOWING_IMG = "/images/towing-ai-section-NYLcfeoN8UVBr4WX8S9MTg.webp";
const AUTOSHOP_IMG = "/images/autoshop-ai-section-it8EHW45wsQTEpx4Rtjocm.webp";
const CONVINI_IMG = "/images/convini-flagship-hero-GdmYmzXoEHArM3HGDwLRRb.webp";

/* ── Flagship Brands (featured panels) ── */
const flagships = [
  {
    name: "Towing AI",
    tagline: "A Blue Collar AI Company",
    description:
      "AI-powered dispatch, route optimization, and fleet management built specifically for the towing industry. Reduce response times, cut fuel costs, and never miss a call again.",
    image: TOWING_IMG,
    icon: Truck,
    accentClass: "text-amber-400",
    borderClass: "border-amber-400/40 hover:border-amber-400/70",
    bgClass: "bg-amber-400/10",
    url: "#contact",
    features: [
      { icon: MapPin, label: "Smart Dispatch & GPS Routing" },
      { icon: Clock, label: "24/7 AI Call Handling" },
      { icon: BarChart3, label: "Fleet Performance Analytics" },
    ],
  },
  {
    name: "AutoShop AI",
    tagline: "A Blue Collar AI Company",
    description:
      "AI-driven customer management, diagnostic assistance, and operational tools designed for auto repair shops, body shops, and service centers. Work smarter, not harder.",
    image: AUTOSHOP_IMG,
    icon: Wrench,
    accentClass: "text-red-400",
    borderClass: "border-red-400/40 hover:border-red-400/70",
    bgClass: "bg-red-400/10",
    url: "#contact",
    features: [
      { icon: Users, label: "Customer Lifecycle Management" },
      { icon: Cog, label: "AI Diagnostic Assistance" },
      { icon: MessageSquare, label: "Automated Review & Follow-up" },
    ],
  },
  {
    name: "CONVINI",
    tagline: "The Convini Ecosystem — Investor Opportunity",
    description:
      "CONVINI is the parent ecosystem powering ConviniCAR, ConviniTRAVEL, ConviniREWARDS, and ConviniTICKETS. One membership unlocks every Convini service — and territory-based investors can own the rights to deliver it in their city.",
    image: CONVINI_IMG,
    icon: Landmark,
    accentClass: "text-electric",
    borderClass: "border-electric/40 hover:border-electric/70",
    bgClass: "bg-electric/10",
    url: "https://www.convinicar.online/",
    external: true,
    features: [
      { icon: Award, label: "One membership, every Convini service" },
      { icon: Briefcase, label: "ConviniCAR · TRAVEL · REWARDS · TICKETS" },
      { icon: Landmark, label: "Buy your city — territory-based investor model" },
    ],
  },
];

/* ── Portfolio Brands (grid cards) ── */
const portfolio = [
  {
    name: "US Tow Alliance",
    domain: "ustowalliance.com",
    description: "The national alliance and network for towing operators — connecting, supporting, and elevating the industry.",
    icon: Database,
    accentClass: "text-indigo-400",
    borderClass: "border-indigo-400/30 hover:border-indigo-400/60",
    bgClass: "bg-indigo-400/10",
  },
  {
    name: "US Tow Dispatch",
    domain: "www.ustowdispatch.com",
    description: "AI-powered dispatch platform for towing operators — automated call routing and smart job assignment.",
    icon: Headphones,
    accentClass: "text-blue-400",
    borderClass: "border-blue-400/30 hover:border-blue-400/60",
    bgClass: "bg-blue-400/10",
  },
  {
    name: "US Tow AI Connect",
    domain: "www.ustowaiconnect.com",
    description: "AI-powered copilot for towing operators — smart routing, automated dispatch, and real-time fleet intelligence.",
    icon: Navigation,
    accentClass: "text-lime-400",
    borderClass: "border-lime-400/30 hover:border-lime-400/60",
    bgClass: "bg-lime-400/10",
  },
  {
    name: "US Tow Fleet",
    domain: "www.ustowfleet.com",
    description: "Fleet protection, compliance monitoring, and risk management.",
    icon: Shield,
    accentClass: "text-violet-400",
    borderClass: "border-violet-400/30 hover:border-violet-400/60",
    bgClass: "bg-violet-400/10",
  },
  {
    name: "US Tow Jobs",
    domain: "www.ustowjobs.com",
    description: "The dedicated job board for the towing industry workforce.",
    icon: Briefcase,
    accentClass: "text-emerald-400",
    borderClass: "border-emerald-400/30 hover:border-emerald-400/60",
    bgClass: "bg-emerald-400/10",
  },
  {
    name: "US Tow Grade",
    domain: "www.ustowgrade.com",
    description: "Performance ratings, reviews, and quality standards for towing.",
    icon: Award,
    accentClass: "text-rose-400",
    borderClass: "border-rose-400/30 hover:border-rose-400/60",
    bgClass: "bg-rose-400/10",
  },
  {
    name: "US Tow Bid",
    domain: "www.ustowbid.com",
    description: "Competitive bidding marketplace connecting towers with customers.",
    icon: Gavel,
    accentClass: "text-amber-400",
    borderClass: "border-amber-400/30 hover:border-amber-400/60",
    bgClass: "bg-amber-400/10",
  },
  {
    name: "US Tow Shield",
    domain: "www.ustowshield.com",
    description: "Protection and security solutions for towing operators — incident reporting, claims documentation, and safety compliance.",
    icon: ShieldAlert,
    accentClass: "text-pink-400",
    borderClass: "border-pink-400/30 hover:border-pink-400/60",
    bgClass: "bg-pink-400/10",
  },
  {
    name: "US Tow Credit",
    domain: "www.creditclaim.online",
    description: "Credit recovery and financial solutions for towing businesses — claim management and revenue protection.",
    icon: CreditCard,
    accentClass: "text-purple-400",
    borderClass: "border-purple-400/30 hover:border-purple-400/60",
    bgClass: "bg-purple-400/10",
  },
  {
    name: "US Tow News",
    domain: "www.ustownews.com",
    description: "Industry news and updates for the towing professional — market intel, regulatory changes, and operator stories.",
    icon: Newspaper,
    accentClass: "text-amber-300",
    borderClass: "border-amber-300/30 hover:border-amber-300/60",
    bgClass: "bg-amber-300/10",
  },
  {
    name: "US Tow X",
    domain: "www.ustowx.com",
    description: "The social network for the US towing industry — connect, share, and engage with operators nationwide.",
    icon: Hash,
    accentClass: "text-cyan-300",
    borderClass: "border-cyan-300/30 hover:border-cyan-300/60",
    bgClass: "bg-cyan-300/10",
  },
  {
    name: "US Tow Command",
    domain: "www.ustowcommand.com",
    description: "Truck and equipment GPS and camera solutions.",
    icon: Radio,
    accentClass: "text-cyan-400",
    borderClass: "border-cyan-400/30 hover:border-cyan-400/60",
    bgClass: "bg-cyan-400/10",
  },
  {
    name: "CONVINI",
    domain: "convinicar.online",
    url: "https://www.convinicar.online/",
    description: "Roadside emergency management app plus convenience benefits — travel, entertainment, insurance, and rewards.",
    icon: Car,
    accentClass: "text-sky-400",
    borderClass: "border-sky-400/30 hover:border-sky-400/60",
    bgClass: "bg-sky-400/10",
  },
  {
    name: "Peer Field Press",
    domain: "peerfieldpress.com",
    url: "https://www.peerfieldpress.com/",
    description: "A library of 10 books written specifically for the towing industry — leadership, operations, and the craft of running a tow business.",
    icon: BookOpen,
    accentClass: "text-fuchsia-400",
    borderClass: "border-fuchsia-400/30 hover:border-fuchsia-400/60",
    bgClass: "bg-fuchsia-400/10",
  },
  {
    name: "Recon AI",
    domain: "reconai.manus.space",
    description: "Messy books? We clean them up. Reconciliation AI is a full-service bookkeeping cleanup and ongoing maintenance solution — we straighten out the mess, then keep your books clean going forward.",
    icon: FileSearch,
    accentClass: "text-green-400",
    borderClass: "border-green-400/30 hover:border-green-400/60",
    bgClass: "bg-green-400/10",
  },
  {
    name: "Recon AI DIY Kit",
    domain: "reconaitool.manus.space",
    description: "The self-service AI toolkit powering Recon AI — intelligent prompts and agent workflows for financial matching, exception handling, and audit-ready reporting.",
    icon: Cog,
    accentClass: "text-lime-400",
    borderClass: "border-lime-400/30 hover:border-lime-400/60",
    bgClass: "bg-lime-400/10",
  },
  {
    name: "Insurance Solutions",
    domain: "insurancesol-prqw5vtw.manus.space",
    description: "Insurance brokers and consultants for the towing industry. We prepare your business year-round for the best policy.",
    icon: ShieldCheck,
    accentClass: "text-teal-400",
    borderClass: "border-teal-400/30 hover:border-teal-400/60",
    bgClass: "bg-teal-400/10",
  },
  {
    name: "Bridge Capital",
    domain: "fuchsia-sallyanne-97.tiiny.site",
    description: "Business financing and capital solutions for blue collar companies.",
    icon: Landmark,
    accentClass: "text-yellow-400",
    borderClass: "border-yellow-400/30 hover:border-yellow-400/60",
    bgClass: "bg-yellow-400/10",
  },
  {
    name: "Wrench Link (Pending)",
    domain: "tomato-gwenora-60.tiiny.site",
    description: "Connecting auto mechanics with shops, parts, and opportunities.",
    icon: Link2,
    accentClass: "text-red-400",
    borderClass: "border-red-400/30 hover:border-red-400/60",
    bgClass: "bg-red-400/10",
  },
  {
    name: "Iron Horse Tow Trucks (Pending)",
    domain: "indigo-aleda-56.tiiny.site",
    description: "Premium tow truck sales, leasing, and equipment solutions.",
    icon: Hammer,
    accentClass: "text-zinc-400",
    borderClass: "border-zinc-400/30 hover:border-zinc-400/60",
    bgClass: "bg-zinc-400/10",
  },
  {
    name: "MCA Debt Solutions",
    domain: "mcaloanproblems.online",
    description: "Merchant cash advance debt restructuring and relief services.",
    icon: Scale,
    accentClass: "text-orange-400",
    borderClass: "border-orange-400/30 hover:border-orange-400/60",
    bgClass: "bg-orange-400/10",
  },
  {
    name: "Shift Track",
    domain: "www.shifttrack.online",
    description: "Know your revenue to the mile and the hour — every day, every week, every month, every year.",
    icon: ClipboardList,
    accentClass: "text-sky-400",
    borderClass: "border-sky-400/30 hover:border-sky-400/60",
    bgClass: "bg-sky-400/10",
  },
];

export default function Subsidiaries() {
  return (
    <section id="subsidiaries" className="py-24 md:py-32 relative bg-navy-deep">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric/30 to-transparent" />

      <div className="container">
        {/* Section Header */}
        <div className="mb-16">
          <Reveal
            as="span"
            from="none"
            className="font-mono text-xs text-ember tracking-[0.3em] uppercase block mb-4"
          >
            // OUR BRANDS
          </Reveal>
          <Reveal
            as="h2"
            from="up"
            duration={0.6}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4"
          >
            A Growing Family of
            <br />
            <span className="text-electric">Blue Collar Brands</span>
          </Reveal>
          <Reveal
            as="p"
            from="up"
            duration={0.6}
            delay={0.1}
            className="text-steel-light text-lg max-w-2xl"
          >
            Blue Collar AI operates through focused subsidiary brands — each purpose-built
            to serve a specific niche in the blue collar economy.
          </Reveal>

          {/* Brand count badge */}
          <Reveal
            from="none"
            delay={0.3}
            className="inline-flex items-center gap-2 mt-6 px-4 py-1.5 border-[2px] border-electric/30 bg-electric/5"
          >
            <span className="font-mono text-2xl font-bold text-electric">{flagships.length + portfolio.length}</span>
            <span className="font-mono text-xs text-steel-light tracking-wider uppercase">Brands & Growing</span>
          </Reveal>
        </div>

        {/* ── Flagship Brands ── */}
        <div className="flex flex-col gap-8 mb-12">
          {flagships.map((sub, i) => (
            <Reveal
              key={i}
              from="up"
              duration={0.6}
              delay={i * 0.15}
              className={`border-[3px] ${sub.borderClass} bg-card transition-all duration-300 overflow-hidden`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Image */}
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <Img
                    src={sub.image}
                    width={1920}
                    height={1072}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    alt={sub.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-card/60 to-transparent lg:from-transparent lg:to-card/80" />
                </div>

                {/* Content */}
                <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase mb-3">
                    {sub.tagline}
                  </span>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 border-[2px] ${sub.borderClass} ${sub.bgClass} flex items-center justify-center`}>
                      <sub.icon className={`w-5 h-5 ${sub.accentClass}`} />
                    </div>
                    <h3 className="font-heading font-bold text-2xl md:text-3xl text-white">
                      {sub.name}
                    </h3>
                  </div>
                  <p className="text-steel-light text-base leading-relaxed mb-8">
                    {sub.description}
                  </p>
                  <div className="flex flex-col gap-3 mb-8">
                    {sub.features.map((feat, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <feat.icon className={`w-4 h-4 ${sub.accentClass} shrink-0`} />
                        <span className="text-sm text-white font-medium">{feat.label}</span>
                      </div>
                    ))}
                  </div>
                  <a
                    href={sub.url}
                    target={(sub as any).external ? "_blank" : undefined}
                    rel={(sub as any).external ? "noopener noreferrer" : undefined}
                    className="inline-flex items-center gap-2 font-heading font-semibold text-sm text-white group"
                  >
                    Learn More About {sub.name}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ── Portfolio Grid ── */}
        <div className="mb-8">
          <span className="font-mono text-xs text-steel tracking-[0.2em] uppercase">
            // FULL PORTFOLIO
          </span>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4"
        >
          {portfolio.map((brand, i) => (
            <Reveal
              as="a"
              key={i}
              from="up"
              duration={0.4}
              delay={i * 0.06}
              href={(brand as any).url ?? ((brand as any).isInternal ? `/${brand.domain}` : `https://${brand.domain}`)}
              target={(brand as any).isInternal ? undefined : "_blank"}
              rel={(brand as any).isInternal ? undefined : "noopener noreferrer"}
              className={`group relative p-5 border-[3px] ${brand.borderClass} bg-card transition-all duration-300 hover:-translate-y-1 block`}
            >
              {/* Icon */}
              <div className={`w-10 h-10 border-[2px] ${brand.borderClass} ${brand.bgClass} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <brand.icon className={`w-5 h-5 ${brand.accentClass}`} />
              </div>

              {/* Name */}
              <h4 className="font-heading font-bold text-base text-white mb-1 flex items-center gap-2">
                {brand.name}
                <ExternalLink className="w-3 h-3 text-steel opacity-0 group-hover:opacity-100 transition-opacity" />
              </h4>

              {/* Domain */}
              <p className="font-mono text-[10px] text-steel tracking-wider mb-3">
                {brand.domain}
              </p>

              {/* Description */}
              <p className="text-steel-light text-xs leading-relaxed">
                {brand.description}
              </p>

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-6 h-6 border-b-[2px] border-l-[2px] border-current opacity-0 group-hover:opacity-20 transition-opacity" />
            </Reveal>
          ))}
        </div>

      </div>
    </section>
  );
}
